import type { ColorPaletteConfiguration } from '@/ui/color-picker/ui-color-picker.component';
import type { BusinessIntelligenceBarChartComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-bar-chart/business-intelligence-bar-chart';
import type { BusinessIntelligenceHistogramComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-histogram/business-intelligence-histogram';
import type { BusinessIntelligenceLinesChartComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-lines-chart/business-intelligence-lines-chart';
import type { BusinessIntelligencePieChartComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-pie-chart/business-intelligence-pie-chart';
import type { BusinessIntelligencePivotTableComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-pivot-table/business-intelligence-pivot-table';
import type { BusinessIntelligenceScatterChartComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-scatter-chart/business-intelligence-scatter-chart';
import type { BusinessIntelligenceSingleValueComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-single-value/business-intelligence-single-value';
import type { BusinessIntelligenceTableComponentConfig } from '../components/business-intelligence-data-representations/business-intelligence-table/business-intelligence-table';
import {
    type BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberType,
    type BusinessIntelligenceSortCriterion
} from './BusinessIntelligence.interface';
import type { BusinessIntelligenceDimensionConfiguration, BusinessIntelligenceMeasureConfiguration } from './TailoredWidget.interface';

export enum BusinessIntelligenceRepresentationCode {
    BAR_CHART = 'BAR_CHART',
    HISTOGRAM = 'HISTOGRAM',
    LINE_CHART = 'LINE_CHART',
    PIE_CHART = 'PIE_CHART',
    SCATTER_CHART = 'SCATTER_CHART',
    SINGLE_VALUE = 'SINGLE_VALUE',
    PIVOT_TABLE = 'PIVOT_TABLE',
    TABLE = 'TABLE'
}

export interface BusinessIntelligenceBarChartRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    categorization: BusinessIntelligenceDimensionConfiguration | null;
    segmentation: BusinessIntelligenceDimensionConfiguration | null;
    measure: { measureCode: BusinessIntelligenceMeasureCode };
    sortCriteria: BusinessIntelligenceSortCriterion[];
    limit?: number;
    shouldZeroBeDisplayed: boolean; // this param is resolved by the front and is override if the limit is set
}

export interface BusinessIntelligenceHistogramRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    xAxis: BusinessIntelligenceDimensionConfiguration;
    segmentation: BusinessIntelligenceDimensionConfiguration | null;
    click: BusinessIntelligenceDimensionConfiguration | null;
    measure: { measureCode: BusinessIntelligenceMeasureCode };
    sortCriteria: BusinessIntelligenceSortCriterion[];
    limit: number | null;
    shouldZeroBeDisplayed: boolean; // this param is resolved by the front and is override if the limit is set
}

export interface BusinessIntelligenceScatterChartRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    xAxis: { measureCode: BusinessIntelligenceMeasureCode };
    yAxis: { measureCode: BusinessIntelligenceMeasureCode };
    groupBy: BusinessIntelligenceDimensionConfiguration;
    pointSize: { measureCode: BusinessIntelligenceMeasureCode } | null;
    segmentation: BusinessIntelligenceDimensionConfiguration | null;
    click: BusinessIntelligenceDimensionConfiguration | null;
}

export enum BusinessIntelligenceComboChartMeasureChartType {
    LINE = 'line',
    COLUMN = 'column'
}

export interface BusinessIntelligenceLinesChartRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    xAxis: BusinessIntelligenceDimensionConfiguration;
    segmentation: BusinessIntelligenceDimensionConfiguration | null;
    measures: {
        measureCode: BusinessIntelligenceMeasureCode;
        isCumulative: boolean;
        chartType: BusinessIntelligenceComboChartMeasureChartType;
        columnStackingGroup: number | null;
        color: ColorPaletteConfiguration | null;
    }[];
    displayZeros: boolean | null;
}

export interface BusinessIntelligencePieChartRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    measure: { measureCode: BusinessIntelligenceMeasureCode };
    segmentation: BusinessIntelligenceDimensionConfiguration | null;
    areDataLabelsActivated?: boolean;
}

export interface BusinessIntelligenceSingleValueRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    measure: { measureCode: BusinessIntelligenceMeasureCode };
    caption: string;
}

export interface BusinessIntelligenceMeasureMemberConfiguration {
    type: BusinessIntelligenceMemberType.MEASURE;
    memberConfiguration: BusinessIntelligenceMeasureConfiguration;
}

export interface BusinessIntelligenceDimensionMemberConfiguration {
    type: BusinessIntelligenceMemberType.DIMENSION;
    memberConfiguration: BusinessIntelligenceDimensionConfiguration;
}

export type BusinessIntelligenceMemberConfiguration =
    | BusinessIntelligenceMeasureMemberConfiguration
    | BusinessIntelligenceDimensionMemberConfiguration;

export function isBusinessIntelligenceMeasure(
    member: BusinessIntelligenceMemberConfiguration
): member is BusinessIntelligenceMeasureMemberConfiguration {
    return (member as BusinessIntelligenceMeasureMemberConfiguration).type === BusinessIntelligenceMemberType.MEASURE;
}

export function isBusinessIntelligenceDimension(
    member: BusinessIntelligenceMemberConfiguration
): member is BusinessIntelligenceDimensionMemberConfiguration {
    return (member as BusinessIntelligenceDimensionMemberConfiguration).type === BusinessIntelligenceMemberType.DIMENSION;
}

export interface BusinessIntelligenceTableRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    members: BusinessIntelligenceMemberConfiguration[];
    sortCriteria: BusinessIntelligenceSortCriterion[];
    displayLinesWithNullValues: boolean;
    limit?: number;
}

export interface BusinessIntelligencePivotTableRepresentationConfig extends BusinessIntelligenceRepresentationConfig {
    lines: BusinessIntelligenceDimensionConfiguration[];
    pivot: BusinessIntelligenceDimensionConfiguration;
    measure: { measureCode: BusinessIntelligenceMeasureCode };
    sortCriteria: BusinessIntelligenceSortCriterion[];
}

export interface BusinessIntelligenceRepresentationConfig {}

export interface BusinessIntelligenceRepresentationComponentConfig {}

export type BusinessIntelligenceRepresentationCodeConfig<T extends BusinessIntelligenceRepresentationCode> = {
    [BusinessIntelligenceRepresentationCode.BAR_CHART]: BusinessIntelligenceBarChartRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.HISTOGRAM]: BusinessIntelligenceHistogramRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.PIE_CHART]: BusinessIntelligencePieChartRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.LINE_CHART]: BusinessIntelligenceLinesChartRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.PIVOT_TABLE]: BusinessIntelligencePivotTableRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.SINGLE_VALUE]: BusinessIntelligenceSingleValueRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.SCATTER_CHART]: BusinessIntelligenceScatterChartRepresentationConfig;
    [BusinessIntelligenceRepresentationCode.TABLE]: BusinessIntelligenceTableRepresentationConfig;
}[T];

export type AnyBusinessIntelligenceRepresentationConfig =
    | BusinessIntelligenceBarChartRepresentationConfig
    | BusinessIntelligenceHistogramRepresentationConfig
    | BusinessIntelligencePieChartRepresentationConfig
    | BusinessIntelligencePivotTableRepresentationConfig
    | BusinessIntelligenceLinesChartRepresentationConfig
    | BusinessIntelligenceSingleValueRepresentationConfig
    | BusinessIntelligenceScatterChartRepresentationConfig
    | BusinessIntelligenceTableRepresentationConfig;

export type BusinessIntelligenceRepresentationCodeComponentConfig<T extends BusinessIntelligenceRepresentationCode> = {
    [BusinessIntelligenceRepresentationCode.BAR_CHART]: BusinessIntelligenceBarChartComponentConfig;
    [BusinessIntelligenceRepresentationCode.HISTOGRAM]: BusinessIntelligenceHistogramComponentConfig;
    [BusinessIntelligenceRepresentationCode.PIE_CHART]: BusinessIntelligencePieChartComponentConfig;
    [BusinessIntelligenceRepresentationCode.LINE_CHART]: BusinessIntelligenceLinesChartComponentConfig;
    [BusinessIntelligenceRepresentationCode.PIVOT_TABLE]: BusinessIntelligencePivotTableComponentConfig;
    [BusinessIntelligenceRepresentationCode.SINGLE_VALUE]: BusinessIntelligenceSingleValueComponentConfig;
    [BusinessIntelligenceRepresentationCode.SCATTER_CHART]: BusinessIntelligenceScatterChartComponentConfig;
    [BusinessIntelligenceRepresentationCode.TABLE]: BusinessIntelligenceTableComponentConfig;
}[T];

export type AnyBusinessIntelligenceRepresentationComponentConfig =
    | BusinessIntelligenceBarChartComponentConfig
    | BusinessIntelligenceHistogramComponentConfig
    | BusinessIntelligencePieChartComponentConfig
    | BusinessIntelligencePivotTableComponentConfig
    | BusinessIntelligenceLinesChartComponentConfig
    | BusinessIntelligenceSingleValueComponentConfig
    | BusinessIntelligenceScatterChartComponentConfig
    | BusinessIntelligenceTableComponentConfig;
